/**
 * Client-side mirror of the US-266 citation asset API contract
 * (BIMComply.Web.Citations.Assets.CitationAssetModels /
 * CitationAssetsController, `/api/v1/citation-assets`). Field names and
 * shapes must track the server contract exactly since they are the public
 * wire format; see CitationAssetModels.cs for the source of truth.
 */

export type CitationAssetKind = 'text' | 'table' | 'figure';

export interface CitationAccessPolicy {
  license: string;
  access_level: string;
  allow_public_distribution: boolean;
  usage: string;
  attribution?: string | null;
}

export interface CitationBoundingBox {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CitationAssetDescriptor {
  urn: string;
  package_version: string;
  asset_id: string;
  kind: CitationAssetKind | string;
  source_code: string;
  edition: string;
  authority: string;
  page?: string | null;
  anchor?: string | null;
  mime_type: string;
  byte_size: number;
  sha256: string;
  width_px?: number | null;
  height_px?: number | null;
  bounding_box?: CitationBoundingBox | null;
  access_policy: CitationAccessPolicy;
  retired?: boolean;
}

/**
 * Stable error codes from `ProblemDetails.Extensions.code`. Must stay in
 * sync with `CitationAssetErrorCodes` in CitationAssetModels.cs.
 */
export const CitationAssetErrorCodes = {
  UrnRequired: 'urn_required',
  PackageRequired: 'package_required',
  AssetRequired: 'asset_required',
  InvalidUrn: 'invalid_urn',
  UnsupportedAsset: 'unsupported_asset_kind',
  NotFound: 'citation_asset_not_found',
  Retired: 'citation_asset_retired',
  PackageMismatch: 'citation_package_mismatch',
  AccessDenied: 'citation_asset_access_denied',
  LicenceBlocked: 'citation_asset_licence_blocked',
  UnsupportedMedia: 'citation_asset_unsupported_media',
  TamperDetected: 'citation_asset_tamper_detected',
} as const;

export type CitationAssetErrorCode =
  (typeof CitationAssetErrorCodes)[keyof typeof CitationAssetErrorCodes] | 'unknown_error';

export interface CitationAssetKey {
  urn: string;
  packageVersion: string;
  assetId: string;
}
